/**
 * API admin : demandes d'activation à distance (reçus de paiement, gratuités, bourses).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export type StatutDemande = 'en_attente' | 'complement_demande' | 'acceptee' | 'rejetee' | 'cloturee';
export type TypeDemande = 'paiement' | 'gratuite' | 'bourse' | 'autre';

export interface DemandeActivation {
  id: number;
  matricule: string;
  nom_complet: string;
  classe: string;
  niveau: string;
  type_demande: TypeDemande;
  statut: StatutDemande;
  periodes_concernees: string[];
  periodes_validees: string[];
  montant_declare: string | null;
  montant_valide: string | null;
  mode_paiement: string;
  reference_recu: string;
  date_paiement_declaree: string | null;
  photo_url: string | null;
  photo_complement_url: string | null;
  message_etudiant: string;
  message_caisse: string;
  motif_rejet: string;
  date_creation: string;
  date_traitement: string | null;
  traite_par_nom: string;
  paiement_courant?: {
    connu: boolean;
    a_jour: boolean;
    periodes_a_jour: string[];
    periodes_en_dette: string[];
    demande_couverte: boolean;
  };
  /** 2026-09-13 : étudiant déjà à jour de paiement → demande écartée des listes à traiter */
  etudiant_a_jour?: boolean;
}

export interface DemandesResponse {
  results: DemandeActivation[];
  total: number;
  nb_a_traiter: number;
  /** 2026-09-13 : demandes en cours écartées car l'étudiant est déjà à jour de paiement */
  nb_ecartees_a_jour?: number;
}

export const demandesActivationApi = createApi({
  reducerPath: 'demandesActivationApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['DemandesActivation'],
  endpoints: (builder) => ({
    getDemandesActivation: builder.query<DemandesResponse, {
      statut?: 'a_traiter' | StatutDemande | 'all';
      q?: string;
      limit?: number;
    } | void>({
      query: (params) => ({
        url: '/portail/admin/demandes-activation/',
        params: params ?? undefined,
      }),
      providesTags: ['DemandesActivation'],
    }),

    validerDemande: builder.mutation<
      { success: boolean; statut: string; nb_periodes_validees: number },
      { id: number; montant_valide?: string; periodes_validees?: string[]; message_caisse?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/portail/admin/demandes-activation/${id}/valider/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DemandesActivation'],
    }),

    rejeterDemande: builder.mutation<
      { success: boolean; statut: string },
      { id: number; motif_rejet: string; message_caisse?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/portail/admin/demandes-activation/${id}/rejeter/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DemandesActivation'],
    }),

    demanderComplement: builder.mutation<
      { success: boolean; statut: string },
      { id: number; message_caisse: string }
    >({
      query: ({ id, message_caisse }) => ({
        url: `/portail/admin/demandes-activation/${id}/complement/`,
        method: 'POST',
        body: { message_caisse },
      }),
      invalidatesTags: ['DemandesActivation'],
    }),

    annulerValidationDemande: builder.mutation<
      { success: boolean; nouveau_statut: string; nb_etats_modifies: number },
      { id: number; motif: string }
    >({
      query: ({ id, motif }) => ({
        url: `/portail/admin/demandes-activation/${id}/annuler-validation/`,
        method: 'POST',
        body: { motif },
      }),
      invalidatesTags: ['DemandesActivation'],
    }),
  }),
});

export const {
  useGetDemandesActivationQuery,
  useValiderDemandeMutation,
  useRejeterDemandeMutation,
  useDemanderComplementMutation,
  useAnnulerValidationDemandeMutation,
} = demandesActivationApi;
